import Image from '@/components/Image';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/routing';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import React from 'react';
import ProductCart from './ProductCart';
import { TCart } from '@/types/carts';
import { PRODUCT_IMAGE_SIZE } from '@/lib/utils/command';
import SlideUpAnimation from '@/components/animation/SlideUpAnimation';
import { useLocale, useTranslations } from 'next-intl';
import { Routes } from '@/lib/routes/routes';
import useCartStore from '@/lib/state-stores/cartStore';
import SAR from '@/public/SAR';
import CurrentLanguageText from '@/components/helper/CurrentLanguageText';

type TCartProps = {
  carts: TCart[];
  onCloseCartSheet?: () => void;
};

const StoreCart = ({ carts, onCloseCartSheet }: TCartProps) => {
  const { deleteCart } = useCartStore();

  const lang = useLocale();
  const t = useTranslations('cart');

  const handleCloseSheet = () => {
    if (onCloseCartSheet) onCloseCartSheet();
  };

  return (
    <>
      {carts.map((item, index) => (
        <div key={index} className="mb-3 flex flex-col gap-3 rounded-lg bg-white p-3 shadow-sm">
          {/* store details */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                href={
                  item.cartType === 3 ? Routes.digitalCards : Routes.branchDetails(item.branch.id)
                }
                onClick={handleCloseSheet}
              >
                <Image
                  key={item.branch.store.logoUrl}
                  src={item.branch.store.logoUrl}
                  size={PRODUCT_IMAGE_SIZE.MEDIUM}
                  width={50}
                  height={50}
                  alt={item.branch.englishName}
                  className="h-14 w-14 rounded-lg border-2 border-slate-200 object-cover"
                />
              </Link>
              <div className="flex flex-col">
                <h3 className="text-lg font-bold">
                  <CurrentLanguageText
                    arabicName={item.branch.store.arabicName}
                    englishName={item.branch.store.englishName}
                  />
                </h3>
                <p className="text-gray-5 text-sm md:text-base">
                  <CurrentLanguageText
                    arabicName={item.branch.arabicName}
                    englishName={item.branch.englishName}
                  />
                </p>
              </div>
            </div>
            <Link
              href={
                item.cartType === 3 ? Routes.digitalCards : Routes.branchDetails(item.branch.id)
              }
              onClick={handleCloseSheet}
              className="bg-primary flex h-7 w-7 items-center justify-center rounded-full text-white"
            >
              {lang === 'ar' ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
            </Link>
          </div>

          {/* number of products at cart  */}
          <p>
            <span className="font-bold">{t('cart-item', { item: item.cartItems.length })}</span>
          </p>
          {/* list of products in cart */}
          {item.cartItems.map((cart) => (
            <ProductCart
              key={cart.id}
              cart={cart}
              branchId={item.branch.id}
              onCloseCartSheet={onCloseCartSheet}
              cardType={item.cartType}
            />
          ))}

          {/* checkout and clear products from cart buttons */}

          <div className="flex w-full items-center justify-between gap-4 lg:gap-8">
            <button
              onClick={() => deleteCart(item.id)}
              className="basis-1/3 border-0 text-sm font-bold underline outline-hidden"
            >
              {t('clear-cart')}
            </button>
            {item.canCheckout ? (
              <Link href={Routes.checkout(item.id)}>
                <Button
                  rounded={'full'}
                  className={`flex basis-3/2 text-lg font-normal ltr:flex-row-reverse`}
                  disabled={!item.canCheckout}
                  onClick={handleCloseSheet}
                >
                  {t('checkout')}
                  <div className="mx-1 font-bold">
                    <SlideUpAnimation uniqueKey={item.totalPrice?.toFixed(2)}>
                      {item.totalPrice?.toFixed(2)}
                    </SlideUpAnimation>
                  </div>
                  <SAR width={16} height={16} className={`h-auto w-auto`} />
                </Button>
              </Link>
            ) : (
              <Button
                rounded={'full'}
                className={`flex basis-3/2 text-lg font-normal lg:text-xl ltr:flex-row-reverse`}
                disabled={!item.canCheckout}
              >
                {t('checkout')}
                <div className="mx-2 font-bold">
                  <SlideUpAnimation uniqueKey={item.totalPrice?.toFixed(2)}>
                    {item.totalPrice?.toFixed(2)}
                  </SlideUpAnimation>
                </div>
                <SAR width={16} height={16} className={`h-auto w-auto`} />
              </Button>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default StoreCart;
